import type { Concept2Workout } from '../types/concept2';

const PACE_500_TYPES = new Set(['rower', 'skierg', 'dynamic', 'slides', 'paddle', 'water']);
const PACE_1000_TYPES = new Set(['bike']);

export function formatConcept2Duration(timeTenths: number | null | undefined): string | null {
  if (timeTenths == null || timeTenths < 0) {
    return null;
  }

  const totalSeconds = timeTenths / 10;
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const tenths = Math.round(totalSeconds * 10) % 10;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${tenths}`;
  }

  return `${minutes}:${String(seconds).padStart(2, '0')}.${tenths}`;
}

export function paceDistanceForMachine(machineType: string | null | undefined): 500 | 1000 | null {
  if (!machineType) {
    return null;
  }
  if (PACE_500_TYPES.has(machineType)) {
    return 500;
  }
  if (PACE_1000_TYPES.has(machineType)) {
    return 1000;
  }
  return null;
}

export function calculateConcept2Pace(
  timeTenths: number | null | undefined,
  distanceMeters: number | null | undefined,
  machineType: string | null | undefined
): string | null {
  const paceDistance = paceDistanceForMachine(machineType);
  if (
    paceDistance === null ||
    timeTenths == null ||
    timeTenths <= 0 ||
    distanceMeters == null ||
    distanceMeters <= 0
  ) {
    return null;
  }

  const paceTenths = Math.round((timeTenths * paceDistance) / distanceMeters);
  return formatConcept2Duration(paceTenths);
}

export function formatMachineTypeLabel(machineType: string): string {
  switch (machineType) {
    case 'rower':
      return 'RowErg';
    case 'skierg':
      return 'SkiErg';
    case 'bike':
      return 'BikeErg';
    case 'multierg':
      return 'MultiErg';
    case 'dynamic':
      return 'Dynamic';
    case 'slides':
      return 'Slides';
    default:
      return machineType;
  }
}

export function formatUnavailable(value: string | null | undefined): string {
  return value && value.length > 0 ? value : '—';
}

export function concept2WorkoutLocalDate(workout: Concept2Workout): string {
  return workout.workout_local_at.slice(0, 10);
}

export function formatConcept2WorkoutPickerLabel(workout: Concept2Workout): string {
  const dateLabel = (() => {
    try {
      return new Date(workout.workout_local_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return workout.workout_local_at.replace('T', ' ').slice(0, 16);
    }
  })();

  const machine = formatMachineTypeLabel(workout.machine_type);
  const distanceLabel =
    workout.distance_meters != null
      ? `${workout.distance_meters} m`
      : formatConcept2Duration(workout.time_tenths) || '—';

  return `${dateLabel} · ${distanceLabel} ${machine}`;
}

export function parseConcept2WorkoutDate(workout: Concept2Workout): Date {
  const [year, month, day] = concept2WorkoutLocalDate(workout).split('-').map(Number);
  return new Date(year, month - 1, day);
}

export interface Concept2HeartRateSummary {
  average: number | null;
  min: number | null;
  max: number | null;
  ending: number | null;
}

function integerField(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function parseConcept2HeartRate(value: unknown): Concept2HeartRateSummary {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { average: null, min: null, max: null, ending: null };
  }

  const record = value as Record<string, unknown>;
  return {
    average: integerField(record.average),
    min: integerField(record.min),
    max: integerField(record.max),
    ending: integerField(record.ending),
  };
}

export function averageConcept2Watts(
  wattminutesTotal: number | null | undefined,
  timeTenths: number | null | undefined
): number | null {
  if (wattminutesTotal == null || wattminutesTotal <= 0 || timeTenths == null || timeTenths <= 0) {
    return null;
  }

  const minutes = timeTenths / 600;
  if (minutes <= 0) {
    return null;
  }

  return Math.round(wattminutesTotal / minutes);
}

export function caloriesPerHour(
  caloriesTotal: number | null | undefined,
  timeTenths: number | null | undefined
): number | null {
  if (caloriesTotal == null || caloriesTotal <= 0 || timeTenths == null || timeTenths <= 0) {
    return null;
  }

  const hours = timeTenths / 36000;
  if (hours <= 0) {
    return null;
  }

  return Math.round(caloriesTotal / hours);
}
